import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import {
  getActiveWorklogs,
  getSubmittedWorklogs,
  getForemanDataWorklogs,
  fetchDeleteActiveSheet,
  fetchDeleteCrewSheet,
  fetchDeleteSubmittedSheet,
  setChangeArchivedActiveState,
  setChangeArchivedCrewState,
} from './worklogsOperations';

export const sheetsAdapter = createEntityAdapter({ selectId: (sheet) => sheet._id });

const initialState = sheetsAdapter.getInitialState({
  activeIds: [],
  submittedIds: [],
  foremanIds: [],
  sheetIds: [], // navigation order read by WorklogDetails (reversed for active)
  hasMore: { active: true, submitted: true, foreman: true },
  sheetsLength: 0,
  shouldBeToday: false,
  isLoading: false,
  forceUpdate: false,
  error: null,
  submissionCertGenerates: false,
  currentRequestId: { active: null, submitted: null, foreman: null },
});

const removeFromList = (state, listKey, id) => {
  state[listKey] = state[listKey].filter((x) => x !== id);
  sheetsAdapter.removeOne(state, id);
};

const appendTab = (state, listKey, { data, navIds, clear }) => {
  const ids = data.map((s) => s._id);
  if (clear) {
    const stale = state[listKey].filter((id) => !ids.includes(id));
    if (stale.length) sheetsAdapter.removeMany(state, stale);
  }
  sheetsAdapter.upsertMany(state, data);
  state[listKey] = clear ? ids : [...state[listKey], ...ids];
  state.sheetIds = clear ? navIds : [...state.sheetIds, ...navIds];
  state.forceUpdate = false;
  state.isLoading = false;
};

const slice = createSlice({
  name: 'worklogs',
  initialState,
  reducers: {
    setError(state, { payload }) {
      state.error = payload;
      state.isLoading = false;
    },
    setForceUpdate(state, { payload }) {
      state.forceUpdate = payload;
      state.isLoading = false;
    },
    setCertGenerates(state, { payload }) {
      state.submissionCertGenerates = payload;
    },
    resetWorklogs() {
      return initialState;
    },
    // Socket-driven single-sheet refresh: replace the stored sheet in place if present.
    setRefreshActiveSheet(state, { payload }) {
      if (payload?._id && state.entities[payload._id]) sheetsAdapter.setOne(state, payload);
      state.isLoading = false;
    },
    setRefreshCrewSheet(state, { payload }) {
      if (payload?._id && state.entities[payload._id]) sheetsAdapter.setOne(state, payload);
      state.isLoading = false;
    },
    // Submitted items match by their nested sheetId, so update the wrapper's `sheet` field.
    setRefreshSubmittedSheet(state, { payload }) {
      state.submittedIds.forEach((id) => {
        const item = state.entities[id];
        if (item && item.sheetId == payload?._id) item.sheet = payload;
      });
      state.isLoading = false;
    },
    setDeletedActiveSheet(state, { payload }) {
      removeFromList(state, 'activeIds', payload);
      state.isLoading = false;
    },
    setDeletedCrewSheet(state, { payload }) {
      removeFromList(state, 'foremanIds', payload);
      state.isLoading = false;
    },
    // Socket-driven bulk merge of active sheets (project/contractor live edits).
    activeSheetsUpdated(state, { payload }) {
      sheetsAdapter.upsertMany(state, payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActiveWorklogs.pending, (state, action) => {
        state.currentRequestId.active = action.meta.requestId;
        state.isLoading = true;
      })
      .addCase(getActiveWorklogs.fulfilled, (state, action) => {
        // Drop out-of-order responses: only the latest in-flight request may write the list.
        if (action.meta.requestId !== state.currentRequestId.active) return;
        appendTab(state, 'activeIds', action.payload);
        state.sheetsLength = state.activeIds.length;
        state.shouldBeToday = action.payload.shouldBeToday;
        state.hasMore.active = action.payload.hasMore;
      })
      .addCase(getActiveWorklogs.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId.active) return;
        state.error = action.payload ?? action.error;
        state.isLoading = false;
      })
      .addCase(getSubmittedWorklogs.pending, (state, action) => {
        state.currentRequestId.submitted = action.meta.requestId;
        state.isLoading = true;
      })
      .addCase(getSubmittedWorklogs.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId.submitted) return;
        appendTab(state, 'submittedIds', action.payload);
        state.hasMore.submitted = action.payload.hasMore;
      })
      .addCase(getSubmittedWorklogs.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId.submitted) return;
        state.error = action.payload ?? action.error;
        state.isLoading = false;
      })
      .addCase(getForemanDataWorklogs.pending, (state, action) => {
        state.currentRequestId.foreman = action.meta.requestId;
        state.isLoading = true;
      })
      .addCase(getForemanDataWorklogs.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId.foreman) return;
        appendTab(state, 'foremanIds', action.payload);
        state.hasMore.foreman = action.payload.hasMore;
      })
      .addCase(getForemanDataWorklogs.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId.foreman) return;
        state.error = action.payload ?? action.error;
        state.isLoading = false;
      })
      .addCase(fetchDeleteActiveSheet.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchDeleteActiveSheet.fulfilled, (state, { payload: id }) => {
        removeFromList(state, 'activeIds', id);
        state.isLoading = false;
      })
      .addCase(fetchDeleteCrewSheet.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchDeleteCrewSheet.fulfilled, (state, { payload: id }) => {
        removeFromList(state, 'foremanIds', id);
        state.isLoading = false;
      })
      .addCase(fetchDeleteSubmittedSheet.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchDeleteSubmittedSheet.fulfilled, (state, { payload: id }) => {
        removeFromList(state, 'submittedIds', id);
        state.isLoading = false;
      })
      .addCase(setChangeArchivedActiveState.fulfilled, (state, { payload: id }) => {
        const sheet = state.entities[id];
        if (sheet) sheet.archived = !sheet.archived;
        state.isLoading = false;
      })
      .addCase(setChangeArchivedCrewState.fulfilled, (state, { payload: id }) => {
        const sheet = state.entities[id];
        if (sheet) sheet.archived = !sheet.archived;
        state.isLoading = false;
      })
      .addMatcher(
        (action) =>
          /^worklogs\/.*\/rejected$/.test(action.type) &&
          !/^worklogs\/get(Active|Submitted|Foreman)\/rejected$/.test(action.type),
        (state, action) => {
          state.error = action.payload ?? action.error;
          state.isLoading = false;
        }
      );
  },
});

export const worklogsActions = slice.actions;

export default slice.reducer;
